import { useAuth0 } from "@auth0/auth0-react";
import axios, { type AxiosResponse } from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import isEqual from "lodash/isEqual";

import { useAppContext } from "../AppProviders";
import { useCompaniesFetchAll } from "../api/CompaniesApi";
import type { CompaniesApi } from "../api/Companies";
import type { ThreeSixtyFinishedRequest } from "../api/ThreeSixty";
import type { ThreeSixtyFinishedRow } from "../api/ThreeSixty";
import { API_URL, isMocksEnabled } from "../api/helpers";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import {
  useGetThreeSixtyFinishedList,
  useGetThreeSixtyInvestigatingList,
  useGetThreeSixtyQAList,
  useGetThreeSixtyQueueingList,
  useGetThreeSixtyWebSearchList,
} from "../api/ThreeSixtyAPI";
import type { ReportSummaryItemJson } from "../api/threeSixtyLists360";
import { CompareUiListSyncButton } from "../reports/CompareUiListSyncButton";
import { THREE_SIXTY_COMPARE_UI_LIST_BY_ROW_ID } from "../reports/compareUiListBackfill";
import {
  CompareCountsCell,
  type CompareProgress,
  type OnComparePageProgress,
} from "../reports/compareProgress";

const COMPARE_PAGE_SIZE = 100;
const FINISHED_LEGACY_PAGE_SIZE = 20;
/** Max pages per side when walking legacy / lists APIs (same idea as misconduct compare). */
const MAX_COMPARE_PAGES = 80;

type CompareCompanyFilter = {
  companyUuid: string;
  companyName: string;
};

type CompareGetParams = Record<string, string | number | undefined>;

type CompareGetFn = (
  url: string,
  params: CompareGetParams,
  companiesHeader?: string
) => Promise<AxiosResponse<{ timestamp?: number } & Record<string, unknown>>>;

type PagedCompareRows = {
  rows: Comparable360Row[];
  truncated: boolean;
  reportedTotal: number | null;
};

/** Narrow fields shared enough between legacy and lists rows to diff meaningfully. */
type Comparable360Row = {
  request_uuid: string;
  company_uuid: string;
};

export type ListCompareResult = {
  id: string;
  title: string;
  v2025Label: string;
  v2026Label: string;
  v2025Endpoint: string;
  v2026Endpoint: string;
  v2025Count: number | null;
  v2026Count: number | null;
  onlyInV2025: string[];
  onlyInV2026: string[];
  commonUuids: number;
  mismatchedInCommon: number;
  v2025Ms: number | null;
  v2026Ms: number | null;
  v2025Error: string | null;
  v2026Error: string | null;
  note?: string;
  compareTruncated?: boolean;
};

type ThreeSixtyCompareTabConfig = {
  id: string;
  title: string;
  v2025Endpoint: string;
  v2026Endpoint: string;
  fetchV2025: (onPageLoaded?: OnComparePageProgress) => Promise<PagedCompareRows>;
  fetch2026: (onPageLoaded?: OnComparePageProgress) => Promise<PagedCompareRows>;
};

type CompareTabRowState = {
  id: string;
  title: string;
  v2025Endpoint: string;
  v2026Endpoint: string;
  result: ListCompareResult | null;
  progress: CompareProgress | null;
  running: boolean;
  onCompare: () => void;
};

type ReportSummaryListResponseJson = {
  report_summaries?: ReportSummaryItemJson[] | null;
  total_rows?: number | null;
};

function companyIdFromApi(item: CompaniesApi): string {
  return item.external_id ?? item.name;
}

async function compareApiGet(
  accessToken: string,
  url: string,
  params: CompareGetParams,
  companiesHeader?: string
): Promise<AxiosResponse> {
  const headers: Record<string, string> = { Authorization: accessToken };
  if (companiesHeader) {
    headers.companies_header = companiesHeader;
  }
  const search = new URLSearchParams();
  for (const [key, val] of Object.entries(params)) {
    if (val === undefined) continue;
    search.set(key, String(val));
  }
  const qs = search.toString();
  return axios.get(qs ? `${url}?${qs}` : url, { headers });
}

function errMsg(e: unknown): string {
  if (e instanceof Error) return e.message;
  return String(e);
}

/** Stop when the API returns an empty page or all reported rows are loaded. */
function shouldStopComparePagination(
  rawPageRowCount: number,
  loadedCount: number,
  reportedTotal: number | null
): boolean {
  if (rawPageRowCount === 0) return true;
  if (reportedTotal != null && loadedCount >= reportedTotal) return true;
  return false;
}

function comparableFromSummaryItem(
  item: ReportSummaryItemJson
): Comparable360Row | null {
  if (!item.report_uuid) return null;
  return {
    request_uuid: String(item.report_uuid),
    company_uuid: item.company_uuid != null ? String(item.company_uuid) : "",
  };
}

function rowsFromSummaryListBody(body: ReportSummaryListResponseJson): Comparable360Row[] {
  return (body.report_summaries ?? [])
    .map(comparableFromSummaryItem)
    .filter((x): x is Comparable360Row => x != null);
}

function comparableFromLegacyRow(row: {
  request_uuid?: string;
  company_uuid?: string;
}): Comparable360Row | null {
  if (!row.request_uuid) return null;
  return {
    request_uuid: String(row.request_uuid),
    company_uuid: String(row.company_uuid ?? ""),
  };
}

function legacyRowsToComparable<T extends { request_uuid?: string; company_uuid?: string }>(
  rows: T[],
  filterCompanyUuid?: string
): Comparable360Row[] {
  const filtered = filterCompanyUuid
    ? rows.filter((r) => r.company_uuid === filterCompanyUuid)
    : rows;
  return filtered
    .map(comparableFromLegacyRow)
    .filter((x): x is Comparable360Row => x != null);
}

async function paginateLists360Summaries(
  compareGet: CompareGetFn,
  listsPath: string,
  companiesHeader?: string,
  onPageLoaded?: OnComparePageProgress
): Promise<PagedCompareRows> {
  const out: Comparable360Row[] = [];
  let reportedTotal: number | null = null;
  let truncated = false;
  for (let page = 1; page <= MAX_COMPARE_PAGES; page++) {
    const offset = (page - 1) * COMPARE_PAGE_SIZE;
    const response = await compareGet(
      `${API_URL}${listsPath}`,
      { offset, size: COMPARE_PAGE_SIZE },
      companiesHeader
    );
    const body = response.data as unknown as ReportSummaryListResponseJson;
    if (reportedTotal == null) reportedTotal = body.total_rows ?? null;
    const rawPageRowCount = body.report_summaries?.length ?? 0;
    const chunk = rowsFromSummaryListBody(body);
    out.push(...chunk);
    onPageLoaded?.(out.length, reportedTotal);
    if (shouldStopComparePagination(rawPageRowCount, out.length, reportedTotal)) {
      break;
    }
    if (page === MAX_COMPARE_PAGES && reportedTotal != null && out.length < reportedTotal) {
      truncated = true;
    }
  }
  if (reportedTotal != null && out.length < reportedTotal) {
    truncated = true;
  }
  return { rows: out, truncated, reportedTotal };
}

async function paginateFinishedLegacy(
  mutate: (
    args: ThreeSixtyFinishedRequest
  ) => Promise<[number, ThreeSixtyFinishedRow[]]>,
  companyFilter: CompareCompanyFilter | null,
  onPageLoaded?: OnComparePageProgress
): Promise<PagedCompareRows> {
  const out: Comparable360Row[] = [];
  let reportedTotal: number | null = null;
  let truncated = false;
  const base: ThreeSixtyFinishedRequest = {
    company_uuids: companyFilter ? [companyFilter.companyUuid] : [],
    blueprint_uuids: [],
    sortField: "finished_at",
    reverse: true,
    page: 1,
  };
  for (let page = 1; page <= MAX_COMPARE_PAGES; page++) {
    const [total, rows] = await mutate({ ...base, page });
    if (reportedTotal == null) reportedTotal = total;
    const rawPageRowCount = rows.length;
    const chunk = legacyRowsToComparable(rows);
    out.push(...chunk);
    onPageLoaded?.(out.length, reportedTotal);
    if (
      shouldStopComparePagination(rawPageRowCount, out.length, reportedTotal)
    ) {
      break;
    }
    if (page === MAX_COMPARE_PAGES && reportedTotal != null && out.length < reportedTotal) {
      truncated = true;
    }
  }
  if (reportedTotal != null && out.length < reportedTotal) {
    truncated = true;
  }
  return { rows: out, truncated, reportedTotal };
}

function buildCompareNote(
  v2025: PagedCompareRows,
  v2026: PagedCompareRows,
  extra?: string
): { note: string; compareTruncated: boolean } {
  const compareTruncated = v2025.truncated || v2026.truncated;
  const parts: string[] = [];
  if (compareTruncated) {
    parts.push(
      `Compare capped at ${MAX_COMPARE_PAGES} pages (size ${COMPARE_PAGE_SIZE}; finished legacy ${FINISHED_LEGACY_PAGE_SIZE}). Legacy loaded ${v2025.rows.length}` +
        (v2025.reportedTotal != null ? ` / total_rows ${v2025.reportedTotal}` : "") +
        `; lists loaded ${v2026.rows.length}` +
        (v2026.reportedTotal != null ? ` / total_rows ${v2026.reportedTotal}` : "") +
        `. Sync disabled.`
    );
  } else {
    parts.push(
      `All pages loaded (lists size ${COMPARE_PAGE_SIZE}). Legacy ${v2025.rows.length}` +
        (v2025.reportedTotal != null ? ` / total_rows ${v2025.reportedTotal}` : "") +
        `; lists ${v2026.rows.length}` +
        (v2026.reportedTotal != null ? ` / total_rows ${v2026.reportedTotal}` : "") +
        `.`
    );
  }
  if (extra) parts.push(extra);
  return { note: parts.join(" "), compareTruncated };
}

function analyzePair(
  id: string,
  title: string,
  v2025Label: string,
  v2026Label: string,
  v2025Endpoint: string,
  v2026Endpoint: string,
  rowsV2025: Comparable360Row[] | null,
  rows2026: Comparable360Row[] | null,
  tV2025: number | null,
  t2026: number | null,
  errV2025: string | null,
  err2026: string | null,
  note?: string,
  compareTruncated?: boolean
): ListCompareResult {
  const a = rowsV2025 ?? [];
  const b = rows2026 ?? [];
  const setA = new Set(a.map((r) => r.request_uuid));
  const setB = new Set(b.map((r) => r.request_uuid));
  const onlyInV2025 = [...setA].filter((u) => !setB.has(u)).sort();
  const onlyInV2026 = [...setB].filter((u) => !setA.has(u)).sort();
  const common = [...setA].filter((u) => setB.has(u));
  const mapA = new Map(a.map((r) => [r.request_uuid, r]));
  const mapB = new Map(b.map((r) => [r.request_uuid, r]));
  let mismatchedInCommon = 0;
  for (const u of common) {
    if (!isEqual(mapA.get(u), mapB.get(u))) {
      mismatchedInCommon += 1;
    }
  }
  return {
    id,
    title,
    v2025Label,
    v2026Label,
    v2025Endpoint,
    v2026Endpoint,
    v2025Count: errV2025 ? null : a.length,
    v2026Count: err2026 ? null : b.length,
    onlyInV2025,
    onlyInV2026,
    commonUuids: common.length,
    mismatchedInCommon,
    v2025Ms: tV2025,
    v2026Ms: t2026,
    v2025Error: errV2025,
    v2026Error: err2026,
    note,
    compareTruncated,
  };
}

async function runComparePair(
  config: ThreeSixtyCompareTabConfig,
  extraNote?: string,
  onProgress?: (progress: CompareProgress) => void
): Promise<ListCompareResult> {
  let rowsV2025: Comparable360Row[] | null = null;
  let rows2026: Comparable360Row[] | null = null;
  let tV2025: number | null = null;
  let t2026: number | null = null;
  let eV2025: string | null = null;
  let e2026: string | null = null;
  let note = extraNote ?? "";
  let compareTruncated = false;
  const t0 = performance.now();
  let pagedV2025: PagedCompareRows | null = null;
  let v2025Loaded = 0;
  let v2025ReportedTotal: number | null = null;
  onProgress?.({
    phase: "v2025",
    v2025Loaded: 0,
    v2026Loaded: 0,
    v2025ReportedTotal: null,
    v2026ReportedTotal: null,
  });
  try {
    pagedV2025 = await config.fetchV2025((loaded, reportedTotal) => {
      v2025Loaded = loaded;
      if (reportedTotal != null) v2025ReportedTotal = reportedTotal;
      onProgress?.({
        phase: "v2025",
        v2025Loaded: loaded,
        v2026Loaded: 0,
        v2025ReportedTotal: reportedTotal ?? v2025ReportedTotal,
        v2026ReportedTotal: null,
      });
    });
    rowsV2025 = pagedV2025.rows;
    v2025Loaded = pagedV2025.rows.length;
    v2025ReportedTotal = pagedV2025.reportedTotal;
    tV2025 = Math.round(performance.now() - t0);
  } catch (e) {
    eV2025 = errMsg(e);
  }
  const t1 = performance.now();
  let paged2026: PagedCompareRows | null = null;
  onProgress?.({
    phase: "v2026",
    v2025Loaded,
    v2026Loaded: 0,
    v2025ReportedTotal,
    v2026ReportedTotal: null,
  });
  try {
    paged2026 = await config.fetch2026((loaded, reportedTotal) => {
      onProgress?.({
        phase: "v2026",
        v2025Loaded,
        v2026Loaded: loaded,
        v2025ReportedTotal,
        v2026ReportedTotal: reportedTotal,
      });
    });
    rows2026 = paged2026.rows;
    t2026 = Math.round(performance.now() - t1);
  } catch (e) {
    e2026 = errMsg(e);
  }
  if (pagedV2025 && paged2026) {
    const built = buildCompareNote(pagedV2025, paged2026, extraNote);
    note = built.note;
    compareTruncated = built.compareTruncated;
  }
  return analyzePair(
    config.id,
    config.title,
    "Legacy GET /360/… or XPage walk",
    "GET /lists/360/… (paged summaries)",
    config.v2025Endpoint,
    config.v2026Endpoint,
    rowsV2025,
    rows2026,
    tV2025,
    t2026,
    eV2025,
    e2026,
    note,
    compareTruncated
  );
}

function ThreeSixtyCompareTable({
  tabs,
  canSync,
  compareAllRunning,
  onCompareAll,
  anyLoaded,
}: {
  tabs: CompareTabRowState[];
  canSync: boolean;
  compareAllRunning: boolean;
  onCompareAll: () => void;
  anyLoaded: boolean;
}) {
  return (
    <div className="overflow-x-auto border border-gray-400 text-sm">
      <div className="flex flex-wrap items-center gap-2 p-2 bg-gray-100 border-b border-gray-400">
        <button
          type="button"
          className="px-2 py-0.5 bg-gray-700 text-white text-xs rounded disabled:opacity-50"
          disabled={
            compareAllRunning || tabs.some((t) => t.running) || isMocksEnabled()
          }
          onClick={onCompareAll}
        >
          {compareAllRunning ? "Comparing all…" : "Compare all tabs (sequential)"}
        </button>
        {anyLoaded ? (
          <span className="text-xs text-gray-600">
            Each tab loads on its own — use per-row Compare to refresh one list.
          </span>
        ) : null}
      </div>
      <table className="min-w-full border-collapse">
        <thead>
          <tr className="bg-gray-200 text-left">
            <th className="border border-gray-400 p-2">List</th>
            <th className="border border-gray-400 p-2">v2025 source</th>
            <th className="border border-gray-400 p-2">v2026 source</th>
            <th className="border border-gray-400 p-2">Counts</th>
            <th className="border border-gray-400 p-2">request_uuid diff</th>
            <th className="border border-gray-400 p-2">
              Comparable row (common)
            </th>
            <th className="border border-gray-400 p-2">ms</th>
            <th className="border border-gray-400 p-2">Errors</th>
            {canSync ? (
              <th className="border border-gray-400 p-2">Sync ui_list</th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {tabs.map((tab) => {
            const r = tab.result;
            const loaded = r != null;
            return (
              <tr key={tab.id} className="align-top">
                <td className="border border-gray-400 p-2 font-semibold">
                  {tab.title}
                  {loaded && r.note ? (
                    <div className="text-xs font-normal text-gray-600 mt-1">
                      {r.note}
                    </div>
                  ) : null}
                  <div className="mt-2">
                    <button
                      type="button"
                      className="px-2 py-0.5 bg-gray-800 text-white text-xs rounded disabled:opacity-50"
                      disabled={
                        tab.running ||
                        compareAllRunning ||
                        isMocksEnabled()
                      }
                      onClick={tab.onCompare}
                    >
                      {tab.running
                        ? "Comparing…"
                        : loaded
                        ? "Re-compare"
                        : "Compare"}
                    </button>
                  </div>
                </td>
                <td className="border border-gray-400 p-2 font-mono text-xs">
                  {tab.v2025Endpoint}
                  {loaded ? (
                    <div className="text-gray-500">{r.v2025Label}</div>
                  ) : null}
                </td>
                <td className="border border-gray-400 p-2 font-mono text-xs">
                  {tab.v2026Endpoint}
                  {loaded ? (
                    <div className="text-gray-500">{r.v2026Label}</div>
                  ) : null}
                </td>
                <td className="border border-gray-400 p-2">
                  <CompareCountsCell
                    progress={tab.progress}
                    result={r}
                    running={tab.running}
                  />
                </td>
                <td className="border border-gray-400 p-2 text-xs max-w-md">
                  {!loaded ? (
                    <span className="text-gray-400">—</span>
                  ) : (
                    <>
                      <div>only v2025 ({r.onlyInV2025.length})</div>
                      <pre className="whitespace-pre-wrap break-all max-h-32 overflow-auto bg-gray-50 p-1">
                        {r.onlyInV2025.slice(0, 40).join("\n")}
                        {r.onlyInV2025.length > 40
                          ? `\n… +${r.onlyInV2025.length - 40} more`
                          : ""}
                      </pre>
                      <div className="mt-2">
                        only v2026 ({r.onlyInV2026.length})
                      </div>
                      <pre className="whitespace-pre-wrap break-all max-h-32 overflow-auto bg-gray-50 p-1">
                        {r.onlyInV2026.slice(0, 40).join("\n")}
                        {r.onlyInV2026.length > 40
                          ? `\n… +${r.onlyInV2026.length - 40} more`
                          : ""}
                      </pre>
                    </>
                  )}
                </td>
                <td className="border border-gray-400 p-2 text-xs">
                  {!loaded ? (
                    <span className="text-gray-400">—</span>
                  ) : (
                    <>
                      <div>common request_uuid: {r.commonUuids}</div>
                      <div
                        className={
                          r.mismatchedInCommon === 0
                            ? "text-green-700"
                            : "text-amber-700"
                        }
                      >
                        unequal comparable fields: {r.mismatchedInCommon}
                      </div>
                    </>
                  )}
                </td>
                <td className="border border-gray-400 p-2 font-mono text-xs whitespace-nowrap">
                  {!loaded ? (
                    <span className="text-gray-400">—</span>
                  ) : (
                    <>
                      <div>
                        v2025: {r.v2025Ms != null ? `${r.v2025Ms}ms` : "—"}
                      </div>
                      <div>
                        v2026: {r.v2026Ms != null ? `${r.v2026Ms}ms` : "—"}
                      </div>
                    </>
                  )}
                </td>
                <td className="border border-gray-400 p-2 text-xs text-red-700 max-w-xs">
                  {!loaded ? (
                    <span className="text-gray-400">—</span>
                  ) : (
                    <>
                      {r.v2025Error ? <div>v2025: {r.v2025Error}</div> : null}
                      {r.v2026Error ? <div>v2026: {r.v2026Error}</div> : null}
                      {!r.v2025Error && !r.v2026Error ? "—" : null}
                    </>
                  )}
                </td>
                {canSync ? (
                  <td className="border border-gray-400 p-2">
                    {!loaded ? (
                      <span className="text-gray-400 text-xs">—</span>
                    ) : tab.id === "amendment" ? (
                      <span className="text-gray-400 text-xs">—</span>
                    ) : (
                      <>
                        <CompareUiListSyncButton
                          rowTitle={r.title}
                          onlyInLegacy={r.onlyInV2025}
                          uiList={
                            THREE_SIXTY_COMPARE_UI_LIST_BY_ROW_ID[r.id] ?? "RUN"
                          }
                          reportType="360"
                          disabled={
                            Boolean(r.v2025Error) ||
                            Boolean(r.compareTruncated)
                          }
                        />
                        {r.compareTruncated ? (
                          <span className="text-amber-700 text-xs mt-1">
                            Sync disabled — compare did not load all pages.
                          </span>
                        ) : null}
                      </>
                    )}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Dev / QA: compare legacy ``/360/…`` list sources with ``GET /lists/360/…`` summaries.
 */
const ThreeSixtyCompare = () => {
  const { companiesFromAuth } = useAppContext();
  const { getAccessTokenSilently } = useAuth0();
  const perms = usePermissionValidator();
  const canSync = perms.canResumeWorkflow;
  const qLegacy = useGetThreeSixtyQueueingList();
  const wsLegacy = useGetThreeSixtyWebSearchList();
  const invLegacy = useGetThreeSixtyInvestigatingList();
  const qaLegacy = useGetThreeSixtyQAList();
  const finLegacy = useGetThreeSixtyFinishedList();
  const {
    mutate: loadCompanies,
    data: companiesData,
    isLoading: companiesLoading,
  } = useCompaniesFetchAll();

  const [selectedCompanyUuid, setSelectedCompanyUuid] = useState("");
  const companies = useMemo(() => companiesData ?? [], [companiesData]);

  const companyFilter = useMemo((): CompareCompanyFilter | null => {
    if (!selectedCompanyUuid) return null;
    const match = companies.find(
      (c) => companyIdFromApi(c) === selectedCompanyUuid
    );
    if (!match) return null;
    return {
      companyUuid: selectedCompanyUuid,
      companyName: match.name,
    };
  }, [selectedCompanyUuid, companies]);

  const compareGet = useCallback<CompareGetFn>(
    async (url, params, companiesHeader) => {
      const token = await getAccessTokenSilently();
      return compareApiGet(token, url, params, companiesHeader);
    },
    [getAccessTokenSilently]
  );

  const companiesHeader = companyFilter?.companyUuid;
  const filterCompanyUuid = companyFilter?.companyUuid;

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const legacyXPageNote = filterCompanyUuid
    ? "Legacy /360 XPage loaders have no server company filter; rows filtered client-side by company_uuid."
    : undefined;

  const compareTabConfigs = useMemo((): ThreeSixtyCompareTabConfig[] => {
    const legacyPaged = async (
      load: () => Promise<
        { request_uuid?: string; company_uuid?: string }[]
      >,
      onPageLoaded?: OnComparePageProgress
    ): Promise<PagedCompareRows> => {
      onPageLoaded?.(0, null);
      const rows = legacyRowsToComparable(await load(), filterCompanyUuid);
      onPageLoaded?.(rows.length, rows.length);
      return { rows, truncated: false, reportedTotal: rows.length };
    };

    return [
      {
        id: "queuing",
        title: "Queuing",
        v2025Endpoint: "/360/queueing",
        v2026Endpoint: "/lists/360/queuing",
        fetchV2025: (onPageLoaded) =>
          legacyPaged(() => qLegacy.mutateAsync(false), onPageLoaded),
        fetch2026: (onPageLoaded) =>
          paginateLists360Summaries(
            compareGet,
            "/lists/360/queuing",
            companiesHeader,
            onPageLoaded
          ),
      },
      {
        id: "web_search",
        title: "Web search",
        v2025Endpoint: "/360/web_search",
        v2026Endpoint: "/lists/360/web_search",
        fetchV2025: (onPageLoaded) =>
          legacyPaged(() => wsLegacy.mutateAsync(false), onPageLoaded),
        fetch2026: (onPageLoaded) =>
          paginateLists360Summaries(
            compareGet,
            "/lists/360/web_search",
            companiesHeader,
            onPageLoaded
          ),
      },
      {
        id: "investigating",
        title: "Investigating",
        v2025Endpoint: "/360/investigating",
        v2026Endpoint: "/lists/360/investigating",
        fetchV2025: (onPageLoaded) =>
          legacyPaged(() => invLegacy.mutateAsync(false), onPageLoaded),
        fetch2026: (onPageLoaded) =>
          paginateLists360Summaries(
            compareGet,
            "/lists/360/investigating",
            companiesHeader,
            onPageLoaded
          ),
      },
      {
        id: "qa",
        title: "QA",
        v2025Endpoint: "/360/qa",
        v2026Endpoint: "/lists/360/qa",
        fetchV2025: (onPageLoaded) =>
          legacyPaged(() => qaLegacy.mutateAsync(false), onPageLoaded),
        fetch2026: (onPageLoaded) =>
          paginateLists360Summaries(
            compareGet,
            "/lists/360/qa",
            companiesHeader,
            onPageLoaded
          ),
      },
      {
        id: "finished",
        title: "Finished",
        v2025Endpoint: "/360/finished",
        v2026Endpoint: "/lists/360/finished",
        fetchV2025: (onPageLoaded) =>
          paginateFinishedLegacy(
            (args) => finLegacy.mutateAsync(args),
            companyFilter,
            onPageLoaded
          ),
        fetch2026: (onPageLoaded) =>
          paginateLists360Summaries(
            compareGet,
            "/lists/360/finished",
            companiesHeader,
            onPageLoaded
          ),
      },
      {
        id: "amendment",
        title: "Amendment",
        v2025Endpoint: "/lists/360/amend",
        v2026Endpoint: "/lists/360/amend",
        fetchV2025: (onPageLoaded) =>
          paginateLists360Summaries(
            compareGet,
            "/lists/360/amend",
            companiesHeader,
            onPageLoaded
          ),
        fetch2026: (onPageLoaded) =>
          paginateLists360Summaries(
            compareGet,
            "/lists/360/amend",
            companiesHeader,
            onPageLoaded
          ),
      },
    ];
  }, [
    compareGet,
    companyFilter,
    companiesHeader,
    filterCompanyUuid,
    qLegacy,
    wsLegacy,
    invLegacy,
    qaLegacy,
    finLegacy,
  ]);

  const [resultsByTabId, setResultsByTabId] = useState<
    Record<string, ListCompareResult>
  >({});
  const [progressByTabId, setProgressByTabId] = useState<
    Record<string, CompareProgress>
  >({});
  const [runningTabId, setRunningTabId] = useState<string | null>(null);
  const [compareAllRunning, setCompareAllRunning] = useState(false);

  useEffect(() => {
    setResultsByTabId({});
    setProgressByTabId({});
  }, [selectedCompanyUuid]);

  const companyFilterNote = companyFilter
    ? `Company filter: ${companyFilter.companyName}.`
    : undefined;

  const compareTab = useCallback(
    async (tabId: string) => {
      const config = compareTabConfigs.find((t) => t.id === tabId);
      if (!config) return;
      setRunningTabId(tabId);
      setProgressByTabId((prev) => ({
        ...prev,
        [tabId]: {
          phase: "v2025",
          v2025Loaded: 0,
          v2026Loaded: 0,
          v2025ReportedTotal: null,
          v2026ReportedTotal: null,
        },
      }));
      try {
        const extra =
          tabId === "amendment"
            ? [
                companyFilterNote,
                "v2025 and v2026 both call GET /lists/360/amend (paged). Counts should match when the same pages load.",
              ]
                .filter(Boolean)
                .join(" ")
            : [companyFilterNote, legacyXPageNote].filter(Boolean).join(" ");
        const result = await runComparePair(
          config,
          extra || undefined,
          (progress) => {
            setProgressByTabId((prev) => ({ ...prev, [tabId]: progress }));
          }
        );
        setResultsByTabId((prev) => ({ ...prev, [tabId]: result }));
      } finally {
        setProgressByTabId((prev) => {
          const next = { ...prev };
          delete next[tabId];
          return next;
        });
        setRunningTabId((current) => (current === tabId ? null : current));
      }
    },
    [compareTabConfigs, companyFilterNote, legacyXPageNote]
  );

  const compareAllTabs = useCallback(async () => {
    setCompareAllRunning(true);
    try {
      for (const config of compareTabConfigs) {
        setRunningTabId(config.id);
        setProgressByTabId((prev) => ({
          ...prev,
          [config.id]: {
            phase: "v2025",
            v2025Loaded: 0,
            v2026Loaded: 0,
            v2025ReportedTotal: null,
            v2026ReportedTotal: null,
          },
        }));
        try {
          const extra =
            config.id === "amendment"
              ? [
                  companyFilterNote,
                  "v2025 and v2026 both call GET /lists/360/amend (paged). Counts should match when the same pages load.",
                ]
                  .filter(Boolean)
                  .join(" ")
              : [companyFilterNote, legacyXPageNote].filter(Boolean).join(" ");
          const result = await runComparePair(
            config,
            extra || undefined,
            (progress) => {
              setProgressByTabId((prev) => ({ ...prev, [config.id]: progress }));
            }
          );
          setResultsByTabId((prev) => ({ ...prev, [config.id]: result }));
        } finally {
          setProgressByTabId((prev) => {
            const next = { ...prev };
            delete next[config.id];
            return next;
          });
          setRunningTabId((current) =>
            current === config.id ? null : current
          );
        }
      }
    } finally {
      setCompareAllRunning(false);
      setRunningTabId(null);
    }
  }, [compareTabConfigs, companyFilterNote, legacyXPageNote]);

  const tableTabs: CompareTabRowState[] = useMemo(
    () =>
      compareTabConfigs.map((config) => ({
        id: config.id,
        title: config.title,
        v2025Endpoint: config.v2025Endpoint,
        v2026Endpoint: config.v2026Endpoint,
        result: resultsByTabId[config.id] ?? null,
        progress: progressByTabId[config.id] ?? null,
        running: runningTabId === config.id,
        onCompare: () => void compareTab(config.id),
      })),
    [compareTabConfigs, resultsByTabId, progressByTabId, runningTabId, compareTab]
  );

  const anyLoaded = Object.keys(resultsByTabId).length > 0;

  const canAccessThreeSixtyCompare =
    perms.canViewAllReportsWithinCompany &&
    companiesFromAuth?.allCompanies === true;

  if (!canAccessThreeSixtyCompare) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="p-4 max-w-[1400px] mx-auto text-gray-900">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-bold">360 list API compare</h1>
        <Link className="text-blue-600 underline text-sm" to="/360/finished">
          ← Back to 360
        </Link>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <label htmlFor="three-sixty-compare-company" className="text-sm font-medium">
          Company
        </label>
        <select
          id="three-sixty-compare-company"
          className="border border-gray-400 rounded px-2 py-1 text-sm min-w-[220px] max-w-full"
          value={selectedCompanyUuid}
          disabled={companiesLoading}
          onChange={(e) => setSelectedCompanyUuid(e.target.value)}
        >
          <option value="">All companies</option>
          {companies.map((c) => {
            const id = companyIdFromApi(c);
            return (
              <option key={id} value={id}>
                {c.name}
              </option>
            );
          })}
        </select>
        {companyFilter ? (
          <span className="text-xs text-gray-600">
            Lists and finished legacy use server filters; queuing / web search /
            investigating / QA legacy XPage walks filter by{" "}
            <code className="bg-gray-100 px-1">company_uuid</code> after fetch.
          </span>
        ) : (
          <span className="text-xs text-gray-600">
            All companies — same scope as your account header.
          </span>
        )}
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Compare one tab at a time (legacy <code className="bg-gray-100 px-1">/360/…</code>{" "}
        vs <code className="bg-gray-100 px-1">GET /lists/360/…</code>). Requires{" "}
        <code className="bg-gray-100 px-1">
          report:view_all_reports_within_company
        </code>{" "}
        and access to all companies (not company-assigned). Green counts when loaded
        row counts match (all pages, up to {MAX_COMPARE_PAGES}×{COMPARE_PAGE_SIZE}{" "}
        per side); <code className="bg-gray-100 px-1">request_uuid</code> columns show
        symmetric difference. “Unequal comparable fields” counts shared{" "}
        <code className="bg-gray-100 px-1">request_uuid</code> rows where{" "}
        <code className="bg-gray-100 px-1">company_uuid</code> differs.
      </p>

      {isMocksEnabled() ? (
        <p className="text-amber-800 text-sm mb-2">
          Mocks are enabled — comparison will fail until mocks are turned off.
        </p>
      ) : null}

      {!canSync ? (
        <p className="text-amber-800 text-sm mb-2">
          <code className="bg-gray-100 px-1">report:resume_workflow</code> is
          required only for Sync ui_list — comparison below is read-only.
        </p>
      ) : null}

      <ThreeSixtyCompareTable
        tabs={tableTabs}
        canSync={canSync}
        compareAllRunning={compareAllRunning}
        onCompareAll={() => void compareAllTabs()}
        anyLoaded={anyLoaded}
      />
    </div>
  );
};

export default ThreeSixtyCompare;
